import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * OverlayScrollbar — a scroll container with a custom, smoothly-fading thumb.
 *
 * Native scrollbars can't be animated (browsers ignore `transition` on
 * `::-webkit-scrollbar-*` and `scrollbar-color`), so a hover/scroll reveal
 * always hard-cuts. This draws its own thin thumb we CAN opacity-transition:
 *
 *   • Desktop (pointer: fine) → native bar is hidden (.desktop-overlay-scroll),
 *     the custom thumb fades in on scroll + hover and fades out after a short
 *     idle delay.
 *   • Touch → nothing is overridden; the OS keeps its native overlay bar.
 *
 * Drop-in: pass the padded/spaced classes via `contentClassName`. The wrapper
 * element (default <div>, override with `as`) becomes the positioning context
 * and the hover target; extra props (data-*, aria-*) spread onto it.
 */
type OverlayScrollbarProps = React.HTMLAttributes<HTMLElement> & {
  /** Wrapper tag — use "nav"/"aside" to keep the landmark. Default "div". */
  as?: React.ElementType;
  /** Classes for the inner, padded, scrolling content. */
  contentClassName?: string;
  /** Classes for the thumb (color/width). Defaults to a neutral fg-muted fill. */
  thumbClassName?: string;
};

const HIDE_DELAY = 1100; // ms of inactivity before the thumb fades out
const MIN_THUMB = 28;     // px — keep the thumb grabbable on long lists

export function OverlayScrollbar({
  as: Tag = "div",
  className,
  contentClassName,
  thumbClassName,
  children,
  ...rest
}: OverlayScrollbarProps) {
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const thumbRef = React.useRef<HTMLDivElement>(null);
  const hideTimer = React.useRef<number | undefined>(undefined);
  // Refs (not state) so the fade-out timer reads live values without re-binding.
  const flags = React.useRef({ hovering: false, dragging: false });

  const [visible, setVisible] = React.useState(false);
  const [overflowing, setOverflowing] = React.useState(false);
  // Desktop only — touch devices keep their native overlay bar. Pointer type
  // doesn't change within a session in practice, so compute once.
  const [enabled] = React.useState(
    () => typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches,
  );

  // Size + position the thumb from the viewport's scroll metrics.
  const sync = React.useCallback(() => {
    const vp = viewportRef.current;
    const thumb = thumbRef.current;
    if (!vp) return;
    const { scrollTop, scrollHeight, clientHeight } = vp;
    const canScroll = scrollHeight - clientHeight > 1;
    setOverflowing(canScroll);
    if (!canScroll || !thumb) return;
    const thumbH = Math.max(MIN_THUMB, (clientHeight / scrollHeight) * clientHeight);
    const top = (clientHeight - thumbH) * (scrollTop / (scrollHeight - clientHeight));
    thumb.style.height = `${thumbH}px`;
    thumb.style.transform = `translate3d(0, ${top}px, 0)`;
  }, []);

  // Reveal now, then queue a fade-out once scrolling/hover/drag stop.
  const reveal = React.useCallback(() => {
    setVisible(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      if (!flags.current.hovering && !flags.current.dragging) setVisible(false);
    }, HIDE_DELAY);
  }, []);

  React.useEffect(() => {
    const vp = viewportRef.current;
    if (!enabled || !vp) return;
    sync();
    const onScroll = () => { sync(); reveal(); };
    vp.addEventListener("scroll", onScroll, { passive: true });
    // Re-measure when the viewport OR its content changes height (nav collapse,
    // permission-gated items loading, window resize — all flow through here).
    const ro = new ResizeObserver(() => sync());
    ro.observe(vp);
    if (contentRef.current) ro.observe(contentRef.current);
    return () => {
      vp.removeEventListener("scroll", onScroll);
      ro.disconnect();
      window.clearTimeout(hideTimer.current);
    };
  }, [enabled, sync, reveal]);

  // Drag-to-scroll: translate vertical pointer movement into scrollTop.
  const onThumbPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const vp = viewportRef.current;
    const thumb = thumbRef.current;
    if (!vp || !thumb) return;
    e.preventDefault();
    flags.current.dragging = true;
    const startY = e.clientY;
    const startScroll = vp.scrollTop;
    const scrollable = vp.scrollHeight - vp.clientHeight;
    const trackMax = vp.clientHeight - thumb.offsetHeight;
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    const onMove = (ev: PointerEvent) => {
      if (trackMax <= 0) return;
      vp.scrollTop = startScroll + ((ev.clientY - startY) / trackMax) * scrollable;
    };
    const onUp = (ev: PointerEvent) => {
      flags.current.dragging = false;
      el.releasePointerCapture(ev.pointerId);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      reveal(); // restart the fade-out countdown after release
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const hoverProps = enabled
    ? {
        onPointerEnter: () => { flags.current.hovering = true; reveal(); },
        onPointerLeave: () => { flags.current.hovering = false; reveal(); },
      }
    : {};

  return (
    <Tag className={cn("relative min-h-0", className)} {...hoverProps} {...rest}>
      <div
        ref={viewportRef}
        className={cn("h-full overflow-y-auto", enabled && "desktop-overlay-scroll")}
      >
        <div ref={contentRef} className={contentClassName}>
          {children}
        </div>
      </div>
      {enabled && overflowing ? (
        <div
          ref={thumbRef}
          onPointerDown={onThumbPointerDown}
          aria-hidden="true"
          className={cn(
            "absolute right-0.5 top-0 w-1.5 rounded-full touch-none select-none",
            "transition-opacity duration-300 ease-out",
            visible ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
            thumbClassName ?? "bg-fg-muted/40 hover:bg-fg-muted/60",
          )}
        />
      ) : null}
    </Tag>
  );
}
